import { 
  Avatar, 
  Button, 
  ReviewCard, 
  Tag, 
  SidebarInfo, 
  Rating,
  PopperoDrawero,
  PopperButton
} from "@pinkyhead/dovekit";
import { ReactComponent as PhoneIcon } from "../../../../../assets/icons/regular/phone.svg"; 
import { ReactComponent as LocationIcon } from "../../../../../assets/icons/regular/location.svg"; 
import { ReactComponent as MoreIcon } from "../../../../../assets/icons/regular/more.svg"; 
import { ReviewLoading } from "../../../../modules/LoadingPlaceholders/ReviewLoading";
import { DetailsLoading } from "../../../../modules/LoadingPlaceholders/DetailsLoading";
import { NoReviews } from "../../../../modules/NoDataPlaceholders/NoReviews";
import { BackButton } from "../../../../common/BackButton";
import { TagIcons } from "../../../../modules/TagIcons";
import { HeaderContainer } from "../../Details.styles";
import { useBasic } from "./Basic.hooks"
import {
  ButtonsContainer,
  ContactContainer,
  Container,
  DisplayName,
  ListContainer,
  OptionsButton,
  ReviewContainer,
  ReviewsLabel,
  SectionLabel
} from "./Basic.styles";
import { useEffect, useRef, useState } from "react";
import { getReviewsData } from "../../../../../context/details/utils";
import { ClaimType, UrlInterface } from "../../../../../types";
import { useDetails } from "../../../../../context/details";

const ReviewList = ({ reviewUrl, userType } : { reviewUrl: UrlInterface, userType: any }) => {
  const [reviewsData, setReviewsData] = useState<any>([]);
  const [reviewsIsLoading, setReviewsIsLoading] = useState(true);
  const { detailsDispatch } = useDetails();
  const reviewsLoaded = useRef(false);
  const handleChangeTab = (id: number) => {
    detailsDispatch({
      type: "setActiveTab",
      data: {
        id: id,
        name: ""
      }
    })
  }
  useEffect(() => {
    if (!reviewsLoaded.current) {
      reviewsLoaded.current = true
      getReviewsData(reviewUrl, 1, userType).then((res) => {
        setReviewsData(res.slice(0, 3));
        setReviewsIsLoading(false);
      })
    }
  }, [])
  return (
    <ListContainer>
      {reviewsIsLoading
        ?
          <>
            <ReviewLoading />
            <ReviewLoading />
            <ReviewLoading />
          </>
        : reviewsData.length > 0
          ? 
            <>
              {reviewsData.map((review: any) => (
                <ReviewContainer>
                  <ReviewCard key={review.details.id} data={{
                    details: review.details,
                    stars: review.stars,
                    reviewedUser: review.reviewedUser
                  }} 
                  actions={{
                    flag: undefined,
                    helpful: undefined,
                  }}
                  reviewType="received"
                  />
                </ReviewContainer>
              ))}
              <Button fullWidth variant="tertiary" onClick={() => handleChangeTab(2)}>More Reviews</Button>
            </>
          : <NoReviews />
      }
    </ListContainer>
  )
}

export const Basic = () => {
  const {
    handleChangeTab, 
    data, 
    goBack,
    isDataLoading,
    drawerOpen,
    drawerShow,
    popperOpen,
    toggleDrawerOpen,
    togglePopperOpen,
    redirectToCoreProfile,
    participationLabel,
    copyToClipboard,
    inputRef,
    url,
    isCopied,
    user,
    redirectToLoginPage,
    showClaimButton,
    redirectToClaim
  } = useBasic(); 
  return (
    <>
      <HeaderContainer>
        <BackButton onClick={goBack} />
      </HeaderContainer>
      {isDataLoading
        ? <DetailsLoading />
        : data
          ?
            <>
              <Container>
                <Avatar 
                  displayName={data.searchName || ""}
                  picture={data.picture || null}
                  size={160}
                  fontSize={48}
                />
                {data.extraFilters && 
                  <TagIcons 
                    isPremium={data.extraFilters.isPremium}
                    isClaimed={data.extraFilters.isClaimed}
                    isRecommended={data.extraFilters.isRecommended}
                  />
                }
                <DisplayName>{data.searchName}</DisplayName>
                {data.speciality &&
                  <Tag name={data.speciality} />
                }
                {(data.rating !== undefined && data.itemTypeLowerCase !== ClaimType.NFP) &&
                  <Rating value={data.rating} /> 
                }
                {(data.rating !== undefined && data.ratingCount !== undefined && data.itemTypeLowerCase !== ClaimType.NFP) &&
                  <ReviewsLabel>
                    {data.rating.toFixed(1)} stars {" - "}
                    <span>
                      {data.ratingCount}
                      {data.ratingCount > 1 ? " reviews" : " review"}
                    </span>
                  </ReviewsLabel>
                }
                <ButtonsContainer>
                  <div>
                    <Button
                      fullWidth
                      onClick={() => redirectToCoreProfile(data.redirectLink)}
                    >
                      View Profile
                    </Button>
                    <PopperoDrawero
                      drawerOpen={drawerOpen}
                      drawerShow={drawerShow}
                      popperOpen={popperOpen}
                      toggleDrawerOpen={toggleDrawerOpen}
                      togglePopperOpen={togglePopperOpen}
                      iconComponent={<OptionsButton>
                        <MoreIcon />
                      </OptionsButton>}                   
                    >
                      <PopperButton onClick={copyToClipboard}>
                        {isCopied ? "Share - link copied" : "Share"}
                      </PopperButton>
                    </PopperoDrawero>
                    <input
                      ref={inputRef}
                      className="link"
                      value={url}
                      readOnly
                      type="hidden"
                    />
                  </div>
                  {(showClaimButton && data.id && data.itemType && !data.extraFilters.isClaimed) &&
                    <Button
                      fullWidth
                      variant="secondary"
                      onClick={() => redirectToClaim(data)}
                    >
                      Claim Profile
                    </Button>
                  }
                  {(showClaimButton && data.extraFilters.isClaimed) &&
                    <Button
                      fullWidth
                      disabled
                      variant="tertiary"
                    >
                      This profile is already claimed. Please contact support.
                    </Button>
                  }
                </ButtonsContainer>
                <div>
                  {data.phoneNumber &&
                    <ContactContainer>
                      <PhoneIcon />
                      <p className="phone">
                        <a href={`tel:${data.phoneNumber}`}>{data.phoneNumber}</a>
                      </p>
                    </ContactContainer>
                  }
                  <ContactContainer>
                    <LocationIcon />
                    <p className="location">{data.address}</p>
                  </ContactContainer>
                  {data.distance &&
                    <p className="distance">{data.distance[0].toFixed(0)} miles away</p>
                  }
                </div>
                <SidebarInfo>
                  {user
                    ?
                      <>
                        <SidebarInfo.Title>More from this {participationLabel}</SidebarInfo.Title>
                        <SidebarInfo.Description>Check out what this {participationLabel} is participating on the DoveMed platform.</SidebarInfo.Description>
                        <SidebarInfo.ButtonsContainer>
                          <Button fullWidth onClick={() => handleChangeTab(1)}>View {participationLabel} participation</Button>
                        </SidebarInfo.ButtonsContainer>
                      </>
                    :
                      <>
                        <SidebarInfo.Title>Login for a better experience</SidebarInfo.Title>
                        <SidebarInfo.Description>We recommend you login or create an account to get more personalised information about your results. </SidebarInfo.Description>
                        <SidebarInfo.ButtonsContainer>
                          <Button fullWidth onClick={redirectToLoginPage}>
                            Login or create account
                          </Button>
                        </SidebarInfo.ButtonsContainer>
                      </>

                  }
                </SidebarInfo>
                {data.itemTypeLowerCase !== ClaimType.NFP &&
                  <div>
                    <SectionLabel>
                      <h2>Reviews {` (${data.ratingCount})`}</h2>
                      <Button 
                        size="small" 
                        onClick={() => redirectToCoreProfile(data.redirectLink)}
                      >
                        Review
                      </Button>
                    </SectionLabel>
                    {(data && data.reviewUrl) &&
                      <ReviewList reviewUrl={data.reviewUrl} userType={data.itemTypeLowerCase} />
                    }
                  </div>
                }
              </Container>
            </>
          :
            <>
              Error
            </>
      }
    </>
  )
}
